import { useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { fetchParentBundles as fetchParentBundlesAction } from 'store/Bundles'

/**
 * @typedef {Object} ParentBundle
 * @property {string} parentBundleId - Unique identifier for the parent bundle
 * @property {string} displayName - Human-readable name for the specialty
 * @property {string} parentName - Parent category name
 * @property {string} [icon] - Optional icon identifier
 */

/**
 * Custom hook for managing parent bundles (medical specialties). Provides
 * access to parent bundles data and loading state from Redux store.
 *
 * @example
 *   // Auto-fetch on mount
 *   const { parentBundles, busy } = useParentBundles({ fetchOnMount: true })
 *
 * @example
 *   // Manual fetch
 *   const { parentBundles, busy, fetchParentBundles } = useParentBundles()
 *   useEffect(() => {
 *     if (!parentBundles) {
 *       fetchParentBundles()
 *     }
 *   }, [])
 *
 * @param {Object} options - Configuration options
 * @param {boolean} [options.fetchOnMount=false] - Whether to automatically
 *   fetch parent bundles on mount. Default is `false`
 * @returns {Object} Hook return value
 * @returns {ParentBundle[] | null} Returns.parentBundles - Array of parent
 *   bundle entities
 * @returns {boolean} Returns.busy - Loading state for parent bundles request
 * @returns {string} Returns.error - Error message if request failed
 * @returns {Function} Returns.fetchParentBundles - Function to trigger parent
 *   bundles fetch
 */
export const useParentBundles = ({ fetchOnMount = true } = {}) => {
  const dispatch = useDispatch()

  // Select parent bundles data from Redux store
  const parentBundles = useSelector(
    state => state.Bundles?.parentBundles?.entities,
  )
  const busy = useSelector(state => state.Bundles?.parentBundles?.busy)
  const error = useSelector(state => state.Bundles?.parentBundles?.error)

  // Memoized fetch function
  const fetchParentBundles = useCallback(() => {
    dispatch(fetchParentBundlesAction())
  }, [dispatch])

  // Auto-fetch on mount if enabled and data doesn't exist
  useEffect(() => {
    if (fetchOnMount && (!parentBundles || parentBundles.length === 0)) {
      fetchParentBundles()
    }
  }, [fetchOnMount])

  return {
    parentBundles: Array.isArray(parentBundles) ? parentBundles : [],
    busy,
    error,
    fetchParentBundles,
  }
}
